import { Resources, type TFunction } from "Translations/Resources";
import { z } from "zod";

const RESOURCES = Resources.Admin.SsoClients.Validation;

// Keep in sync with SsoClientDefinitions on BE.
const CLIENT_ID_PATTERN = /^[a-z0-9][a-z0-9-]{2,63}$/;

const isAcceptableRedirectUri = (value: string) => {
  try {
    const url = new URL(value);
    const isLoopback = ["localhost", "127.0.0.1", "[::1]"].includes(
      url.hostname,
    );
    return (
      url.hash === "" &&
      (url.protocol === "https:" || (url.protocol === "http:" && isLoopback))
    );
  } catch {
    return false;
  }
};

export const getSsoClientSchema = (t: TFunction) =>
  z
    .object({
      clientId: z
        .string()
        .regex(CLIENT_ID_PATTERN, t(RESOURCES.ClientIdInvalid)),
      displayName: z.string().trim().min(1, t(RESOURCES.DisplayNameRequired)),
      isConfidential: z.boolean(),
      redirectUris: z
        .array(z.string())
        .min(1, t(RESOURCES.RedirectUriRequired))
        .refine((uris) => uris.every(isAcceptableRedirectUri), {
          message: t(RESOURCES.RedirectUriInvalid),
        }),
      postLogoutRedirectUris: z
        .array(z.string())
        .refine((uris) => uris.every(isAcceptableRedirectUri), {
          message: t(RESOURCES.RedirectUriInvalid),
        }),
      allowedRoles: z.array(z.string()),
      allowedScopes: z.array(z.string()),
    })
    // A scope is exercised through a context token, and that needs a role to act as.
    .refine(
      (data) => data.allowedScopes.length === 0 || data.allowedRoles.length > 0,
      { message: t(RESOURCES.ScopeRequiresRole), path: ["allowedScopes"] },
    );

export type SsoClientFormData = z.infer<ReturnType<typeof getSsoClientSchema>>;
